import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { useToast } from "@/hooks/useToast"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import usdtQRCode from "@/assets/images/usdt-qr-code.jpeg"
import { useState } from "react"

export function PricingPlans() {
  const { toast } = useToast();
  const USDT_ADDRESS = "0xBF15698CDc07ff811adf65E8D85B8DB6Ab168590";
  const [frequency, setFrequency] = useState("month");

  const prices = {
    day: 3,
    week: 7.99,
    month: 20,
    year: 120, // $20 * 12 months - 50% discount
  };

  const frequencyLabel = {
    day: "day",
    week: "week",
    month: "month",
    year: "year"
  };

  const handlePatreonRedirect = () => {
    window.open('https://www.patreon.com/c/dekkme/membership', '_blank');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(USDT_ADDRESS);
      toast({
        title: "Address copied!",
        description: "The USDT address has been copied to your clipboard.",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try copying the address manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto py-12 px-4">
      <h1 className="text-4xl font-bold text-center mb-8">Choose Your Plan</h1>
      
      <div className="flex justify-center mb-8">
        <RadioGroup
          defaultValue="month"
          value={frequency}
          onValueChange={setFrequency}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="day" id="day" />
            <Label htmlFor="day">Daily</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="week" id="week" />
            <Label htmlFor="week">Weekly</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="month" id="month" />
            <Label htmlFor="month">Monthly</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="year" id="year" />
            <Label htmlFor="year">
              Yearly
              <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                50% off
              </span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Free Tier */}
        <Card className="p-6 flex flex-col">
          <h2 className="text-2xl font-bold mb-4">Free</h2>
          <div className="text-3xl font-bold mb-6">$0</div>
          <ul className="space-y-3 mb-8 flex-grow">
            <li className="flex items-center">
              <span className="mr-2">✓</span> Gets 10 daily free tokens (around 1 card per day)
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Can create up to 1 deck
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Can create up to 10 cards per deck
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Can access Card Roulette
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Basic card templates
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Community features
            </li>
          </ul>
          <Button variant="outline" className="w-full">
            Current Plan
          </Button>
        </Card>

        {/* Starter Tier */}
        <Card className="p-6 flex flex-col border-2 border-primary">
          <div className="absolute -top-3 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">
            Popular
          </div>
          <h2 className="text-2xl font-bold mb-4">Starter</h2>
          <div className="text-3xl font-bold mb-6">
            ${prices[frequency as keyof typeof prices]}/{frequencyLabel[frequency as keyof typeof frequencyLabel]}
            {frequency === "year" && (
              <span className="text-sm text-muted-foreground ml-2">
                (Save 50%)
              </span>
            )}
          </div>
          <ul className="space-y-3 mb-8 flex-grow">
            <li className="flex items-center">
              <span className="mr-2">✓</span> Everything in Free
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Gets 500 tokens per day
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Can create up to 3 decks
            </li>
            <li className="flex items-center">
              <span className="mr-2">✓</span> Can create up to 100 cards per deck
            </li>
            <li className="flex items-center font-bold">
              <span className="mr-2">✓</span> Can access Game Genesis and Blueprints
            </li>
            <li className="flex items-center font-bold">
              <span className="mr-2">✓</span> Priority support
            </li>
          </ul>
          <Button 
            className="w-full mb-2" 
            onClick={handlePatreonRedirect}
          >
            Subscribe on Patreon
          </Button>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full">
                Pay with USDT (ERC-20)
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Pay with USDT (ERC-20)</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col space-y-4">
                <div className="flex justify-center">
                  <img 
                    src={usdtQRCode} 
                    alt="USDT Payment QR Code" 
                    className="w-48 h-48 object-contain rounded-lg"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Scan the QR code or send your payment to the following USDT (ERC-20) address:
                </p>
                <div className="flex items-center space-x-2">
                  <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold">
                    {USDT_ADDRESS}
                  </code>
                  <Button variant="secondary" size="sm" onClick={copyToClipboard}>
                    Copy
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  After sending the payment, please allow up to 24 hours for confirmation and account activation.
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </Card>
      </div>
    </div>
  );
} 